import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useSubcontractorAssignments } from './useSubcontractorAssignments';

function warnNonCritical(message: string, detail?: unknown): void {
  if (import.meta.env.DEV) {
    console.warn(`[UnreadNotifications] ${message}`, detail ?? '');
  }
}

export function useUnreadNotifications() {
  const { user, loading: authLoading, profile } = useAuth();
  const { projectIds } = useSubcontractorAssignments();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const refreshingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const finish = useCallback((count: number) => {
    if (!mountedRef.current) return;
    setUnreadCount(count);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!user || authLoading) {
      finish(0);
      return;
    }

    if (refreshingRef.current) return;
    refreshingRef.current = true;

    if (mountedRef.current) {
      setLoading(true);
    }

    try {
      const role = profile?.user_type ?? 'client';

      let readIds = new Set<string>();
      try {
        const { data: reads, error: readsError } = await supabase
          .from('jphph_notification_reads')
          .select('notification_id')
          .eq('user_id', user.id);

        if (readsError) {
          warnNonCritical('notification reads query failed (non-blocking)', readsError.message);
        } else {
          readIds = new Set((reads || []).map((r) => r.notification_id));
        }
      } catch (readsErr) {
        warnNonCritical('notification reads fetch failed (non-blocking)', readsErr);
      }

      const { data: notifs, error } = await supabase
        .from('jphph_notifications')
        .select('id, is_read, user_id, target_role, title, project_id')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .eq('is_visible', true)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        warnNonCritical('notifications query failed (non-blocking)', error.message);
        finish(0);
        return;
      }

      let filtered = (notifs || []).filter(
        (n) =>
          !n.is_read &&
          !readIds.has(n.id) &&
          (n.user_id === user.id || n.target_role === role || (!n.user_id && !n.target_role))
      );

      if (role === 'subcontractor' && projectIds.length > 0) {
        filtered = filtered.filter((n) => !n.project_id || projectIds.includes(n.project_id));
      }

      finish(filtered.length);
    } catch (err) {
      warnNonCritical('unexpected fetch failure (non-blocking)', err);
      finish(0);
    } finally {
      refreshingRef.current = false;
    }
  }, [user, authLoading, profile, projectIds, finish]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void refresh();
      }, 400);
    };

    const channel = supabase
      .channel('unread-notifications')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jphph_notifications' },
        scheduleRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jphph_notification_reads' },
        scheduleRefresh
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [user, refresh]);

  return { unreadCount, loading, refresh };
}
